// =============================================================================
// network.js — utilitários de validação client-side de IP/CIDR/máscara.
//
// Espelha (em formato browser-friendly) o módulo `api/services/netUtils.js`
// do backend. Reusado por formIpSetRoutes.js e RoutesNew.js para validação
// real antes de enviar requisições.
// =============================================================================

const IPV4_REGEX = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

const IPV6_REGEX = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:))$/;

export const isValidIPv4 = (s) => {
  if (typeof s !== 'string') return false;
  const m = s.match(IPV4_REGEX);
  if (!m) return false;
  return m.slice(1).every((o) => Number(o) >= 0 && Number(o) <= 255);
};

export const isValidIPv6 = (s) => typeof s === 'string' && IPV6_REGEX.test(s);

export const isValidIP = (s) => isValidIPv4(s) || isValidIPv6(s);

export const familyOf = (s) => {
  if (isValidIPv4(s)) return 4;
  if (isValidIPv6(s)) return 6;
  return null;
};

export const maskToPrefix = (mask) => {
  if (typeof mask === 'number') {
    return Number.isInteger(mask) && mask >= 0 && mask <= 32 ? mask : null;
  }
  if (typeof mask !== 'string') return null;
  const trimmed = mask.trim();
  if (trimmed === '') return null;
  if (!trimmed.includes('.')) {
    const n = Number(trimmed);
    return Number.isInteger(n) && n >= 0 && n <= 32 ? n : null;
  }
  const octets = trimmed.split('.').map(Number);
  if (octets.length !== 4 || octets.some((o) => Number.isNaN(o) || o < 0 || o > 255)) return null;
  let prefix = 0;
  let zeros = false;
  for (const o of octets) {
    for (let i = 7; i >= 0; i -= 1) {
      const bit = (o >> i) & 1;
      if (bit === 1) {
        if (zeros) return null;
        prefix += 1;
      } else {
        zeros = true;
      }
    }
  }
  return prefix;
};

// Aceita "10.0.0.0/24", "2a02::/32", "10.0.0.0", "default".
export const parseCidr = (input) => {
  if (typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (trimmed === '') return null;
  if (trimmed === 'default') return { address: 'default', prefix: 0, family: null };
  const slashIdx = trimmed.indexOf('/');
  const addr = slashIdx >= 0 ? trimmed.slice(0, slashIdx) : trimmed;
  const fam = familyOf(addr);
  if (!fam) return null;
  let prefix;
  if (slashIdx >= 0) {
    const n = Number(trimmed.slice(slashIdx + 1));
    const max = fam === 4 ? 32 : 128;
    if (!Number.isInteger(n) || n < 0 || n > max) return null;
    prefix = n;
  } else {
    prefix = fam === 4 ? 32 : 128;
  }
  return { address: addr, prefix, family: fam };
};

export const isValidCidr = (s) => parseCidr(s) !== null;

// IPs reservados rejeitados em campos de configuração (espelha
// `services/pjsipTransportsService.js#FORBIDDEN_IPS` no backend).
export const FORBIDDEN_IPS = new Set(['0.0.0.0', '255.255.255.255']);

export const isForbiddenIp = (s) => FORBIDDEN_IPS.has(String(s || '').trim());

// Valida payload de rota como o backend faria. Retorna { ok, errors: [...] }.
export const validateRoutePayload = (payload) => {
  const errors = [];
  if (!payload || typeof payload !== 'object') {
    return { ok: false, errors: ['Payload inválido.'] };
  }
  const iface = String(payload.iface || '').trim();
  if (!/^[a-z][a-z0-9_.:-]{1,15}$/.test(iface)) errors.push('Interface inválida.');

  const useNew = payload.to !== undefined;
  if (useNew) {
    if (!isValidCidr(payload.to)) errors.push('Destino (to) inválido. Use CIDR como 10.0.0.0/24 ou IPv6.');
    if (payload.via && !isValidIP(payload.via)) errors.push('Gateway (via) inválido.');
    if (payload.metric !== undefined && payload.metric !== '' && payload.metric !== null) {
      const n = Number(payload.metric);
      if (!Number.isInteger(n) || n < 0 || n > 4294967295) errors.push('Métrica inválida.');
    }
    if (String(payload.to).trim() === 'default') {
      errors.push('Rota default não pode ser criada aqui — use a página "Trocar IP" (commit-confirm).');
    }
  } else {
    if (!isValidIPv4(payload.srdest)) errors.push('IP/sub-rede de destino inválido (IPv4).');
    if (maskToPrefix(payload.mask) === null) errors.push('Máscara inválida.');
    if (payload.gw && !isValidIP(payload.gw)) errors.push('Gateway inválido.');
  }
  return { ok: errors.length === 0, errors };
};
